// Helpers para renderizar contenido de Prismic (rich text, links, imágenes).

/**
 * Converts Prismic-like rich text array to HTML string
 * @param {Array} richTextArray - Array of text objects with type, text, and spans
 * @returns {string} HTML string
 */
export function renderRichText(richTextArray) {
  if (!richTextArray || !Array.isArray(richTextArray) || richTextArray.length === 0) {
    return '';
  }

  return richTextArray.map(block => {
    let text = block.text || '';
    
    // Apply spans (formatting like bold, italic, links)
    if (block.spans && block.spans.length > 0) {
      // Sort spans by start position in reverse to apply from end to start
      const sortedSpans = [...block.spans].sort((a, b) => b.start - a.start);
      
      sortedSpans.forEach(span => {
        const before = text.substring(0, span.start);
        const spanText = text.substring(span.start, span.end);
        const after = text.substring(span.end);
        
        switch (span.type) {
          case 'strong':
            text = `${before}<strong>${spanText}</strong>${after}`;
            break;
          case 'em':
            text = `${before}<em>${spanText}</em>${after}`;
            break;
          case 'hyperlink': {
            const url = span.data?.url || '#';
            text = `${before}<a href="${url}" target="_blank" rel="noopener noreferrer">${spanText}</a>${after}`;
            break;
          }
          default:
            break;
        }
      });
    }

    // Wrap in appropriate HTML tag based on type
    switch (block.type) {
      case 'heading1':
        return `<h1>${text}</h1>`;
      case 'heading2':
        return `<h2>${text}</h2>`;
      case 'heading3':
        return `<h3>${text}</h3>`;
      case 'heading4':
        return `<h4>${text}</h4>`;
      case 'heading5':
        return `<h5>${text}</h5>`;
      case 'heading6':
        return `<h6>${text}</h6>`;
      case 'paragraph':
        return `<p>${text}</p>`;
      case 'list-item':
        return `<li>${text}</li>`;
      case 'o-list-item':
        return `<li>${text}</li>`;
      case 'preformatted':
        return `<pre>${text}</pre>`;
      default:
        return `<p>${text}</p>`;
    }
  }).join('');
}

/**
 * Wraps list items in appropriate list tags
 * @param {string} html - HTML string that may contain list items
 * @returns {string} HTML with properly wrapped lists
 */
export function wrapLists(html) {
  // Wrap consecutive <li> tags in <ul>
  let result = html.replace(/(<li>.*?<\/li>)+/g, (match) => {
    // Check if it's an ordered list or unordered
    return `<ul>${match}</ul>`;
  });
  
  return result;
}

/**
 * Complete render function that handles lists properly
 * @param {Array} richTextArray - Array of text objects
 * @returns {string} Complete HTML string
 */
export function renderPrismicContent(richTextArray) {
  const html = renderRichText(richTextArray);
  return wrapLists(html);
}

/**
 * Removes query parameters from Prismic image URLs
 * @param {string} url - The original image URL
 * @returns {string} The URL without query parameters
 */
export function getCleanPrismicImageUrl(url) {
  if (!url) return '';
  return url.split('?')[0];
}
